<template>
	<v-card data-testid="column-filter" class="menu-card-width" >
		<v-list data-testid="column-filter__header-list" class="py-0">
			<v-list-item data-testid="column-filter__header" class="px-3 py-2 tableHeaderBg">
				<v-list-item-content data-testid="column-filter__header-content" class="py-0">
					<v-list-item-title data-testid="column-filter__header-title" class="d-flex justify-space-between align-center">
						<span data-testid="column-filter__title-text" class="font-weight-bold">
							<template v-if="filterStage === 0">Column Actions</template>
							<template v-else-if="filterStage === 1">Filter By</template>
						</span>
						<v-btn data-testid="column-filter__back-btn" v-if="filterStage > 0" icon @click="filterStage = 0" class="mr-1">
							<v-icon data-testid="column-filter__back-icon">mdi-arrow-left</v-icon>
						</v-btn>
					</v-list-item-title>
				</v-list-item-content>
				<v-btn data-testid="column-filter__close-btn" icon @click="onCloseClick">
					<v-icon data-testid="column-filter__close-icon">mdi-close</v-icon>
				</v-btn>
			</v-list-item>
		</v-list>
		<v-divider data-testid="column-filter__header-divider"></v-divider>

		<v-list data-testid="column-filter__content-list" class="py-0">
			<v-list-item data-testid="column-filter__filter-option" v-if="filterStage === 0" @click="openFilter" class="px-3 py-2">
				<v-list-item-icon data-testid="column-filter__filter-icon-wrapper" class="mr-2">
					<v-icon data-testid="column-filter__filter-icon">mdi-filter</v-icon>
				</v-list-item-icon>
				<v-list-item-title data-testid="column-filter__filter-title"><span>Filter by</span></v-list-item-title>
				<v-list-item-action data-testid="column-filter__filter-action">
					<v-icon data-testid="column-filter__filter-chevron">mdi-chevron-right</v-icon>
				</v-list-item-action>
			</v-list-item>

			<!-- Date Range Filter Panel -->
			<DateRangeFilter
				v-if="filterStage === 1 && isDateRangeType"
				:value="currentDateRange"
				@apply="onRangeApply"
				@close="onCloseClick"
				data-testid="column-filter__date-range-filter"
			/>

			<!-- Number Range Filter Panel -->
			<NumberRangeFilter
				v-else-if="filterStage === 1 && isNumberRangeType"
				:value="currentNumberRange"
				:data-range="columnDataRange"
				@apply="onRangeApply"
				@close="onCloseClick"
				data-testid="column-filter__number-range-filter"
			/>

			<!-- Standard Multi-Select Filter Panel -->
			<div data-testid="column-filter__filter-panel" v-else-if="filterStage === 1" class="px-3 py-2">
				<v-text-field
					data-testid="column-filter__search-input"
					v-model="searchText"
					label="Search"
					test-id="filter-search-input"
					dense
					outlined
					hide-details
					prepend-inner-icon="mdi-magnify"
					clearable
					clear-icon="mdi-close-circle"
					@click:clear="searchText=''"
					class="mb-2"
				/>

				<v-divider data-testid="column-filter__search-divider" class="my-2" />
				<div data-testid="column-filter__selected-count" class="d-flex align-center mb-2">
					<v-chip data-testid="column-filter__selected-chip" class="ml-0 mr-2 black--text" label outlined>
						{{ selectedCheckboxCount }} <span class="ml-1"> Selected</span>
					</v-chip>
				</div>
				<div data-testid="column-filter__items-scroll" class="filter-list-scroll">
					<v-list-item data-testid="column-filter__selectall-item" dense class="pl-0">
						<v-checkbox
							data-testid="column-filter__selectall-checkbox"
							color="primary"
							class="mt-0"
							hide-details
							:indeterminate="selectAllIndeterminate"
							:input-value="allBaseSelected"
							@change="toggleSelectAll">
						<template v-slot:label>
							<span>{{ 'select all' }}</span>
						</template>
					</v-checkbox>
					</v-list-item>

					<template v-for="item in displayedColumnItems">
						<v-list-item data-testid="column-filter__item" dense class="pl-0" :key="item.value">
							<v-checkbox
								data-testid="column-filter__item-checkbox"
								color="primary"
								class="mt-0"
								hide-details
								:label="formatItemLabel(item)"
								:input-value="item.status"
								@change="() => toggleItem(item)">
							<template v-slot:label>
								<span>{{ formatItemLabel(item) }}</span>
							</template>
						</v-checkbox>
						</v-list-item>
					</template>
				</div>

				<v-divider data-testid="column-filter__footer-divider" class="my-2" />
				<div data-testid="column-filter__footer" class="d-flex justify-end mt-1" v-if="showApplyButton || showSaveButton">
					<!-- Apply Button: applies filter without persisting -->
					<v-btn
						data-testid="column-filter__apply-btn"
						v-if="showApplyButton"
						outlined
						color="primary"
						class="mr-2"
						@click="onApplyClick"
					><span>Apply</span></v-btn>
					<!-- Save Button: persists filter to backend -->
					<v-tooltip v-if="showSaveButton" top content-class="tooltip-top">
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								data-testid="column-filter__save-btn"
								v-bind="attrs"
								v-on="on"
								color="primary"
								@click="onSaveClick"
							><span>Save</span></v-btn>
						</template>
						<span>Filter will be saved to your account</span>
					</v-tooltip>
				</div>
			</div>
		</v-list>
	</v-card>
</template>
<script>
import DateRangeFilter from './DateRangeFilter.vue';
import NumberRangeFilter from './NumberRangeFilter.vue';

export default {
	name: 'ColumnFilter',
	components: { DateRangeFilter, NumberRangeFilter },
	props: {
		header: { type: Object, required: true },
		tableData: { type: Array, required: true },
		selectedValues: { type: [Array, Object], required: true },
		noneSentinel: { type: String, default: 'NONE_SELECTED' },
		/**
		 * @deprecated Use showSaveButton instead. Kept for backward compatibility.
		 */
		enableSaveOption: { type: Boolean, default: false },
		/**
		 * Whether to show the Apply button (applies filter without persisting)
		 */
		showApplyButton: { type: Boolean, default: true },
		/**
		 * Whether to show the Save button (persists filter to backend)
		 */
		showSaveButton: { type: Boolean, default: false },
		/**
		 * Auto-open directly to filter stage (skip Column Actions menu).
		 * Use when only filter is available (no other actions like sort).
		 */
		autoOpen: { type: Boolean, default: false },
		/**
		 * Whether the parent menu is currently open.
		 * Used to reset filterStage when menu reopens.
		 */
		isOpen: { type: Boolean, default: false },
		/**
		 * When true, retain known filter options if tableData shrinks while a column
		 * filter is active (tabledata-only mode). Disabled when parent supplies
		 * a stable filterOptionsData source.
		 */
		preserveOptionsOnFilter: { type: Boolean, default: true }
	},
	data() {
		return {
			filterStage: 0,
			searchText: '',
			baseItems: [], // [{ value, status }]
			userToggled: {}, // tracks items user explicitly toggled during this session
		};
	},
	computed: {
		/**
		 * Computes the min/max of the column values from tableData (for number-range type)
		 */
		columnDataRange() {
			if (!this.isNumberRangeType) return { min: null, max: null };
			const col = this.header.value;
 			let min = null;
 			let max = null;
 			this.tableData.forEach(row => {
 				const n = parseFloat(row[col]);
 				if (!isNaN(n)) {
 					min = min === null ? n : Math.min(min, n);
 					max = max === null ? n : Math.max(max, n);
 				}
 			});
 			return { min, max };
		},
		/**
		 * Whether this header uses date-range filter type
		 */
		isDateRangeType() {
			return this.header && this.header.type === 'date-range';
		},
		/**
		 * Whether this header uses number-range filter type
		 */
		isNumberRangeType() {
			return this.header && this.header.type === 'number-range';
		},
		/**
		 * Whether this header uses any range filter type (date or number)
		 */
		isRangeType() {
			return this.isDateRangeType || this.isNumberRangeType;
		},
		/**
		 * Current date range value from selectedValues for DateRangeFilter
		 */
		currentDateRange() {
			if (!this.isDateRangeType) return { from: null, to: null };
			const val = this.selectedValues;
			if (val && typeof val === 'object' && !Array.isArray(val)) return val;
			return { from: null, to: null };
		},
		/**
		 * Current number range value from selectedValues for NumberRangeFilter
		 */
		currentNumberRange() {
			if (!this.isNumberRangeType) return { min: null, max: null };
			const val = this.selectedValues;
			if (val && typeof val === 'object' && !Array.isArray(val)) return val;
			return { min: null, max: null };
		},
		/**
		 * Whether a search filter is currently active
		 */
		isSearchActive() {
			return (this.searchText || '').trim().length > 0;
		},
		/**
		 * Set of raw values for items currently visible after search
		 * Used for scoping Select All and Apply to visible items only
		 */
		displayedItemValues() {
			return new Set(this.displayedColumnItems.map(item => item.value));
		},
		/**
		 * Items to display - filtered by search text, sorted with selected first
		 */
		displayedColumnItems() {
			const term = (this.searchText || '').toLowerCase().trim();
			const items = !term
				? this.baseItems
				: this.baseItems.filter(i => {
					const label = (i && i.label != null) ? i.label : this.getFilterLabel(i?.value);
					const haystack = (label || '').toString().toLowerCase();
					return haystack.includes(term);
				});
			return items.slice().sort((a, b) => {
				if (a.status === b.status) return 0;
				return a.status ? -1 : 1; // selected first
			});
		},
		/**
		 * Count of selected items among currently displayed (visible) items
		 */
		selectedCheckboxCount() {
			return this.displayedColumnItems.filter(i => i.status).length;
		},
		/**
		 * Whether all VISIBLE items are selected (scoped to search results when searching)
		 */
		allBaseSelected() {
			const items = this.isSearchActive ? this.displayedColumnItems : this.baseItems;
			return items.length > 0 && items.every(i => i.status);
		},
		/**
		 * Whether any VISIBLE item is selected (scoped to search results when searching)
		 */
		anyBaseSelected() {
			const items = this.isSearchActive ? this.displayedColumnItems : this.baseItems;
			return items.some(i => i.status);
		},
		/**
		 * Three-state Select All: checked, unchecked, or indeterminate (partial)
		 * Scoped to visible items when searching
		 */
		selectAllIndeterminate() {
			return this.anyBaseSelected && !this.allBaseSelected;
		}
	},
	watch: {
		tableData: {
			handler() { this.buildBaseItems(); },
			deep: false
		},
		selectedValues: {
			handler(newVal, oldVal) {
				this.syncStatuses();
				// External clear (e.g. Clear Filter button) — reset search UI and rebuild full option list
				if (this._isFilterActive(oldVal) && !this._isFilterActive(newVal)) {
					this._resetTransientState();
					if (!this.isRangeType) {
						this.buildBaseItems();
					}
				}
			},
			deep: true
		},
		/**
		 * Auto-select matching items as user types in search.
		 * - Items matching search text → checked
		 * - Items NOT matching but in applied filter → stay checked (preserved)
		 * - Items NOT matching and NOT in applied filter → unchecked
		 */
		searchText(newVal) {
			const term = (newVal || '').toLowerCase().trim();
			if (!term) {
				// Search cleared — restore statuses from applied filter and reset toggles
				this.userToggled = {};
				this.syncStatuses();
				return;
			}

			const selected = Array.isArray(this.selectedValues) ? this.selectedValues : [];
			const appliedSet = new Set(selected.filter(v => v !== this.noneSentinel));

			this.baseItems.forEach(item => {
				// Skip items the user explicitly toggled — respect their choice
				if (this.userToggled[item.value]) return;

				const label = (item.label != null ? item.label : this.getFilterLabel(item.value) != null ? this.getFilterLabel(item.value) : '').toString().toLowerCase();
				if (label.includes(term)) {
					item.status = true;
				} else {
					item.status = this._isValueSelected(item.value, appliedSet, selected);
				}
			});
		},
		/**
		 * Clear search text when leaving filter view (back button)
		 */
		filterStage(newVal) {
			if (newVal === 0) {
				this.searchText = '';
			}
		},
		/**
		 * When menu opens, auto-open to filter stage if autoOpen is enabled
		 */
		isOpen(newVal, oldVal) {
			if (newVal && this.autoOpen) {
				this.filterStage = 1;
				this.userToggled = {};
				if (!this.isRangeType) {
					this.buildBaseItems();
				}
			} else if (!newVal && oldVal) {
				// Menu closed (Apply/Save/outside click) — clear search so reopen starts fresh
				this._resetTransientState();
			}
		}
	},
	mounted() {
		if (!this.isRangeType) {
			this.buildBaseItems();
		}
		// Auto-open to filter stage if autoOpen prop is true (skip Column Actions menu)
		if (this.autoOpen) {
			this.filterStage = 1;
		}
	},
	methods: {
		/**
		 * Loose value match (handles number vs string from API vs table row)
		 */
		_valuesMatch(a, b) {
			if (a === b) return true;
			if (a == null || b == null) return false;
			return String(a) === String(b);
		},
		_isValueSelected(value, selectedSet, selectedArray) {
			if (selectedSet && selectedSet.has(value)) return true;
			return (selectedArray || []).some(v => this._valuesMatch(v, value));
		},
		/**
		 * Whether selectedValues represents an active (non-default) filter
		 */
		_isFilterActive(val) {
			if (val == null) return false;
			if (Array.isArray(val)) {
				if (val.length === 0) return false;
				return true;
			}
			if (typeof val === 'object') {
				return Object.values(val).some(v => v !== null && v !== undefined && v !== '');
			}
			return false;
		},
		/**
		 * Reset search text and toggle tracking; optionally reset filter stage
		 */
		_resetTransientState({ resetStage = true } = {}) {
			this.searchText = '';
			this.userToggled = {};
			if (resetStage) {
				this.filterStage = 0;
			}
		},
		openFilter() {
			this.filterStage = 1;
			this.userToggled = {};
			if (!this.isRangeType) {
				this.buildBaseItems();
			}
		},
		/**
		 * Handle apply event from DateRangeFilter or NumberRangeFilter.
		 * Emits the range object (or null for clear) directly to parent.
		 */
		onRangeApply(val) {
			this.$emit('apply', val);
		},
		/**
		 * Build unique filter items from tableData for this column
		 */
		buildBaseItems() {
			// Range filter types do not use checkbox items
			if (this.isRangeType) return;
			const col = this.header.value;
			const selected = Array.isArray(this.selectedValues) ? this.selectedValues.slice() : [];
			const noneSelectedMode = selected.includes(this.noneSentinel);
			const hasActiveFilter = selected.length > 0 && !noneSelectedMode;
			
			// Get unique values from tableData (filterOptionsData or tabledata from parent)
			const fromTableValues = this.tableData
				.map(row => row[col])
				.flatMap(val => (Array.isArray(val) ? val : [val]))
				.filter(v => v !== null && v !== undefined);

			const uniqueFromTable = new Set(fromTableValues);
			const uniqueValues = new Set(uniqueFromTable);

			// tabledata-only mode: tableData may shrink after column filter — keep known options
			if (
				this.preserveOptionsOnFilter
				&& hasActiveFilter
				&& uniqueFromTable.size < this.baseItems.length
			) {
				this.baseItems.forEach(item => uniqueValues.add(item.value));
			}

			// Ensure applied filter selections always appear in the list
			if (hasActiveFilter) {
				selected.forEach(v => {
					if (v !== this.noneSentinel) uniqueValues.add(v);
				});
			}
			
			// Build items list with status based on selectedValues
			this.baseItems = [...uniqueValues].map(value => {
				const label = this.getFilterLabel(value);
				let status;
				if (noneSelectedMode) {
					status = false;
				} else if (hasActiveFilter) {
					status = this._isValueSelected(value, null, selected);
				} else {
					// No filter active - all items are selected by default
					status = true;
				}
				return { value, label, status };
			});
		},
		/**
		 * Sync item statuses with selectedValues prop
		 */
		syncStatuses() {
			// Range filter types do not use checkbox items
			if (this.isRangeType) return;
			const selected = Array.isArray(this.selectedValues) ? this.selectedValues.slice() : [];
			const noneSelectedMode = selected.includes(this.noneSentinel);
			const isFiltered = selected.length > 0 && !noneSelectedMode;
			this.baseItems.forEach(obj => {
				obj.status = noneSelectedMode ? false : (!isFiltered || this._isValueSelected(obj.value, null, selected));
			});
		},
		toggleItem(item) {
			item.status = !item.status;
			this.$set(this.userToggled, item.value, true);
		},
		/**
		 * Toggle Select All - scoped to VISIBLE items only when searching
		 * Hidden items retain their existing selection state
		 * @param {Boolean} val - New checked state
		 */
		toggleSelectAll(val) {
			const checked = !!val;
			if (this.isSearchActive) {
				const visibleValues = this.displayedItemValues;
				this.baseItems.forEach(item => {
					if (visibleValues.has(item.value)) {
						item.status = checked;
						this.$set(this.userToggled, item.value, true);
					}
				});
			} else {
				this.baseItems.forEach(i => {
					i.status = checked;
					this.$set(this.userToggled, i.value, true);
				});
			}
		},
		/**
		 * Handle Apply button click - emits current selection without persisting.
		 * Payload is built from ALL baseItems (visible + hidden by search).
		 */
		onApplyClick() {
			// Always use all baseItems — hidden items retain their status during search
			const anySelected = this.baseItems.some(i => i.status);
			const allSelected = this.baseItems.length > 0 && this.baseItems.every(i => i.status);
			
			let payload;
			if (!anySelected) {
				payload = [this.noneSentinel];
			} else if (allSelected && !this.isSearchActive) {
				// All items selected and no search = clear filter
				payload = [];
			} else {
				// Include ALL checked items (both visible and hidden by search)
				payload = this.baseItems.filter(i => i.status).map(i => i.value);
			}
			this.$emit('apply', payload);
		},
		/**
		 * Handle Save button click - builds payload same as Apply, then emits save event.
		 * Payload is built from ALL baseItems (visible + hidden by search).
		 */
		onSaveClick() {
			// Always use all baseItems — hidden items retain their status during search
			const anySelected = this.baseItems.some(i => i.status);
			const allSelected = this.baseItems.length > 0 && this.baseItems.every(i => i.status);
			
			let payload;
			if (!anySelected) {
				payload = [this.noneSentinel];
			} else if (allSelected && !this.isSearchActive) {
				// All items selected and no search = clear filter
				payload = [];
			} else {
				// Include ALL checked items (both visible and hidden by search)
				payload = this.baseItems.filter(i => i.status).map(i => i.value);
			}
			this.$emit('save', payload);
			this.onCloseClick();
		},
		/**
		 * Handle close button click or any close action.
		 * Resets filter state (search, stage) and emits close event.
		 */
		onCloseClick() {
			this._resetTransientState();
			this.$emit('close');
		},
		getFilterLabel(rawValue) {
			if (rawValue === null || rawValue === undefined || rawValue === '') {
				return '(Blank)';
			}
			const map = this.header?.filterLabelMap;
			if (map) {
				const key = typeof rawValue === 'string' ? rawValue : rawValue.toString();
				if (Object.prototype.hasOwnProperty.call(map, key)) {
					return map[key];
				}
			}
			return rawValue.toString();
		},
		formatItemLabel(item) {
			if (!item) return '';
			if (item.label != null) return item.label;
			return this.getFilterLabel(item.value);
		}
	}
};
</script>
